import { useContext, useMemo, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ProcessPassedModal from "./ProcessPassedModal";
import TenantContext from "../../context/tenant/TenantContext";
import { haveValue } from "../../helper/common";

const ConsentStepModal = ({
  isPopup = false,
  user,
  updateTenantData,
  originalUser,
  loadUserTenant,
  onClose,
  loadSharedProfiles,
  selectedLandlord,
  shareProfileWithLandlord,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { submitConsents } = useContext(TenantContext);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showTickScreen, setShowTickScreen] = useState(false);
  const insurance_service_status = isPopup
    ? selectedLandlord?.insurance_service_status
    : user?.referredId?.insurance_service_status;

  const affidavit_activated = isPopup
    ? selectedLandlord?.affidavit_activated
    : user?.workspaceId?.affidavit_activated;

  const consentSections = useMemo(() => {
    const sections = [];

    if (
      insurance_service_status === true &&
      (!haveValue(user?.consents?.gdpr_consent) ||
        !haveValue(user?.consents?.skor_consent))
    ) {
      sections.push({
        id: "gdprCheck",
        title: _t("rentalInsuranceEvaluation"),
        points: [
          _t("insuranceCompanyWillCheckCreditHistory"),
          _t("usedToCalculateRentDefaultRisk"),
          _t("youCanWithdrawConsentBeforeApproval"),
        ],
        checkboxLabel: _t(
          "iConsentToTheProcessingOfMyPersonalDataForRentalInsuranceSolvencyEvaluation"
        ),
        links: [
          {
            title: _t("viewFullGdprNotice"),
            url: "/pdf/consents/gropo_muto_consent_gdpr.pdf",
          },
          {
            title: _t("skorConsent"),
            url: "/pdf/consents/skor_consent.pdf",
          },
        ],
      });
    }

    if (
      affidavit_activated === true &&
      !haveValue(user?.consents?.declaration_consent)
    ) {
      sections.push({
        id: "declarationCheck",
        title: _t("identityAndFundsDeclaration"),
        points: [
          _t("youInformationIsAccurateAndUpToDate"),
          _t("noOngoingTaxIssuesOrInvestigations"),
          _t("fundsAreFromLegalSourcesAndBelongToYou"),
        ],
        checkboxLabel: _t("iConfirmThisInformationIsAccurateAndFundsAreLegal"),
        links: [
          {
            title: _t("viewFullDeclaration"),
            url: "/pdf/consents/declaration_consent.pdf",
          },
        ],
      });
    }

    return sections;
  }, [user]);

  const [consents, setConsents] = useState(
    consentSections.reduce((acc, section) => {
      acc[section.id] = false;
      return acc;
    }, {})
  );

  const handleToggle = (id) => {
    setConsents((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSubmitConsent = async () => {
    try {
      setSubmitLoading(true);

      let consentPayload = {};
      if (consents.gdprCheck) {
        consentPayload.gdpr_consent = true;
        consentPayload.skor_consent = true;
      }
      if (consents.declarationCheck) {
        consentPayload.declaration_consent = true;
      }
      await submitConsents(consentPayload);
      if (isPopup) {
        if (haveValue(loadSharedProfiles)) {
          await loadSharedProfiles(user?._id, "full");
        }
        shareProfileWithLandlord();
        await loadUserTenant("tenant");
        onClose();
      } else {
        let payload = {
          role: localStorage.getItem("usertype"),
          is_edit: false,
          profile_completed: true,
        };

        await updateTenantData(payload);
        navigate(
          `/home${
            originalUser?.gurantor_added_id?.length === 0 ? "?welcome=true" : ""
          }`
        );
      }
    } catch (error) {
      console.log("Error submitting reason", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const isConsentUnchecked = !Object.values(consents).every(Boolean);

  return (
    <Modal
      show
      centered
      className={`landlord-onboarding-modal landlord-onboarding-last-step usage-selection-modal consent-modal selectAdditionalChecks ${
        !isPopup ? "modal-fixed-screen" : ""
      }`}
    >
      <Modal.Header>
        <Modal.Title>
          <h4>
            {isPopup ? _t("actionRequired") : _t("consentTitleOnboarding")}
          </h4>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-center">
          {isPopup
            ? _t(
                "theLandlordRequiresTenantsToSignTheConsentsBelowInOrderToReviewYourProfile"
              )
            : _t(
                "becauseYouAppliedThroughALandlordUniqueInvitationLinkTheseConsentsAreRequiredToShareYourProfile"
              )}
        </p>

        <div className="cards-comp">
          {consentSections.map((section) => (
            <div key={section.id} className="last-step-card">
              <h3>{section.title}</h3>

              <ul>
                {section.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>

              <div
                style={{
                  backgroundColor: consents[section.id] ? "#EDF8F0" : "#F6F6F6",
                }}
                className="consent-checkbox cursor-pointer"
                onClick={() => handleToggle(section.id)}
              >
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={consents[section.id] || false}
                    readOnly
                    onChange={() => handleToggle(section.id)}
                  />
                  <span className="custom-checkmark"></span>
                </label>

                <div>
                  <p>{section.checkboxLabel}</p>

                  {/* Multiple Separate Links */}
                  <div className="d-flex flex-column custom-mt-2">
                    {section.links?.map((linkObj, index) => (
                      <a
                        key={index}
                        href={linkObj.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline cursor-pointer card-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {linkObj.title}
                        <img
                          src="/images/redirect-link-img.svg"
                          alt=""
                          className="link-icon"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {isPopup && (
          <button
            className="btnNext basicInfoButton greenHover cancelButton footer-btn"
            onClick={onClose}
          >
            {_t("cancel")}
          </button>
        )}
        <button
          disabled={isConsentUnchecked || submitLoading}
          className={`btnNext footer-btn`}
          onClick={() => {
            if (isPopup) {
              handleSubmitConsent();
            } else {
              setShowTickScreen(true);
            }
          }}
        >
          {_t("continue")}
        </button>
      </Modal.Footer>

      <div className="d-flex justify-content-center align-item-center legalBlindingText">
        {_t("theseConsentsAreLegallyBinding")}
      </div>

      <ProcessPassedModal
        heading={_t("onboardingSuccessful")}
        subheading={_t("yourOnboardingHasBeenSuccessfullyCompleted")}
        showPopup={showTickScreen}
        buttonText={"goHome"}
        handleClick={handleSubmitConsent}
      />
    </Modal>
  );
};

export default ConsentStepModal;
